import {
    AddCollectedPaymentDto,
    AddAssignedCardsDto,
    AddCollectionsOnTheWayDto,
    AssignedCollections,
    AssignedInstallments,
    CollectedPayments,
    CollectionsOnTheWay,
    CreateIRRDto,
    EligibleInstallmentRow,
    EligibleInstallmentsResponse,
    IRR,
    IRRListResponse,
    IRRQueryParams,
    IRRResponse,
    IRRbillBooks,
    ListEligibleInstallmentsDto,
    PostponedPayments,
    PostponeCardInstallmentDto,
    PostponeCardInstallmentResult,
    PostponeIrrAssignedDto,
    SetIrrCardPaymentDayDto,
    UpdateIRRDto,
} from '../types/irr';
import {
  RecoveryCommissionApplicationRequest,
  RecoveryCommissionApplyResponse,
  RecoveryCommissionPreviewResponse,
} from '../features/irr/recovery-commission.types';
import { IRR_ELIGIBLE_INSTALLMENTS_TIMEOUT_MS } from '@/config/apiConfig';
import { apiService } from './api';
import { requireEmployeeId } from './userContext';

function unwrapIrrEnvelope<T = any>(response: unknown): { success: boolean; data: T } {
  const raw = response as any;
  const level1 = raw?.data ?? raw;
  const level2 = level1?.data ?? level1;
  const success = Boolean(
    raw?.status === 'SUCCESS' || level1?.success === true || raw?.success === true,
  );
  return { success, data: level2 as T };
}

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((v) => String(v ?? '').trim()).filter(Boolean);
}

function normalizeAssignedInstallments(value: unknown): AssignedInstallments[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry): AssignedInstallments | null => {
      const installmentCardId = String((entry as any)?.installmentCardId ?? '').trim();
      if (!installmentCardId) return null;
      return {
        installmentCardId,
        ICReferance: String((entry as any)?.ICReferance ?? '').trim(),
        creditValue: String((entry as any)?.creditValue ?? '').trim(),
        amount:
          typeof (entry as any)?.amount === 'number'
            ? (entry as any).amount
            : Number.parseFloat(String((entry as any)?.amount ?? '0')) || 0,
        installments: normalizeStringArray((entry as any)?.installments),
      };
    })
    .filter((v): v is AssignedInstallments => v != null);
}

function normalizeAssignedCollections(value: unknown): AssignedCollections[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry): AssignedCollections | null => {
      const customerId = String((entry as any)?.customerId ?? '').trim();
      if (!customerId) return null;
      return {
        customerId,
        mobileNumber: String((entry as any)?.mobileNumber ?? '').trim(),
        location: String((entry as any)?.location ?? '').trim(),
        customerName: String((entry as any)?.customerName ?? '').trim(),
        outstandingAmount:
          typeof (entry as any)?.outstandingAmount === 'number'
            ? (entry as any).outstandingAmount
            : Number.parseFloat(String((entry as any)?.outstandingAmount ?? '0')) || 0,
        assignedInstallments: normalizeAssignedInstallments(
          (entry as any)?.assignedInstallments,
        ),
      };
    })
    .filter((v): v is AssignedCollections => v != null);
}

function normalizeCollectedPayments(value: unknown): CollectedPayments[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry): CollectedPayments | null => {
      const paymentId = String((entry as any)?.paymentId ?? '').trim();
      if (!paymentId) return null;
      const amountRaw = (entry as any)?.amount;
      const amount =
        typeof amountRaw === 'number'
          ? amountRaw
          : Number.parseFloat(String(amountRaw ?? '0')) || 0;
      return {
        paymentId,
        billNumber: String((entry as any)?.billNumber ?? '').trim(),
        ICid: String((entry as any)?.ICid ?? '').trim(),
        amount,
        installments: normalizeStringArray((entry as any)?.installments),
      };
    })
    .filter((v): v is CollectedPayments => v != null);
}

function normalizeCollectionsOnTheWay(value: unknown): CollectionsOnTheWay[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry): CollectionsOnTheWay | null => {
      const paymentId = String((entry as any)?.paymentId ?? '').trim();
      const customerId = String((entry as any)?.customerId ?? '').trim();
      if (!paymentId || !customerId) return null;
      const amountRaw = (entry as any)?.amount;
      const amount =
        typeof amountRaw === 'number'
          ? amountRaw
          : Number.parseFloat(String(amountRaw ?? '0')) || 0;
      return {
        customerId,
        mobileNumber: String((entry as any)?.mobileNumber ?? '').trim(),
        customerName: String((entry as any)?.customerName ?? '').trim(),
        paymentId,
        billNumber: String((entry as any)?.billNumber ?? '').trim(),
        ICid: String((entry as any)?.ICid ?? '').trim(),
        amount,
        installments: normalizeStringArray((entry as any)?.installments),
      };
    })
    .filter((v): v is CollectionsOnTheWay => v != null);
}

function normalizeBillBooks(value: unknown): IRRbillBooks[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry): IRRbillBooks | null => {
      const firstAvailableBillNo = String(
        (entry as any)?.firstAvailableBillNo ?? '',
      ).trim();
      if (!firstAvailableBillNo) return null;
      const availableCountRaw = (entry as any)?.availableCount;
      const availableCount =
        typeof availableCountRaw === 'number'
          ? availableCountRaw
          : Number.parseInt(String(availableCountRaw ?? '0'), 10) || 0;
      return {
        remark: String((entry as any)?.remark ?? '').trim(),
        firstAvailableBillNo,
        availableCount,
      };
    })
    .filter((v): v is IRRbillBooks => v != null);
}

function normalizeEligibleInstallmentRows(value: unknown): EligibleInstallmentRow[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((row): EligibleInstallmentRow | null => {
      const installmentId = String((row as any)?.installmentId ?? '').trim();
      const installmentCardId = String((row as any)?.installmentCardId ?? '').trim();
      const routeId = String((row as any)?.routeId ?? '').trim();
      if (!installmentId || !installmentCardId) return null;
      const feeRaw = (row as any)?.installmentFee;
      const installmentFee =
        typeof feeRaw === 'number' && Number.isFinite(feeRaw) ? feeRaw : undefined;
      return {
        installmentId,
        installmentCardId,
        routeId,
        postponeDate: String((row as any)?.postponeDate ?? '').trim() || undefined,
        paymentdate: String((row as any)?.paymentdate ?? '').trim() || undefined,
        ...(installmentFee !== undefined ? { installmentFee } : {}),
      };
    })
    .filter((v): v is EligibleInstallmentRow => v != null);
}

function normalizeLegacyMissedCollections(
  value: unknown,
): Pick<PostponedPayments, 'paymentId' | 'ICid' | 'amount' | 'installments'>[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      const ICid = String((entry as any)?.ICid ?? '').trim();
      const paymentId = String((entry as any)?.paymentId ?? '').trim();
      if (!ICid || !paymentId) return null;
      const amountRaw = (entry as any)?.amount;
      const amount =
        typeof amountRaw === 'number'
          ? amountRaw
          : Number.parseFloat(String(amountRaw ?? '0')) || 0;
      const inst = Array.isArray((entry as any)?.installments)
        ? normalizeStringArray((entry as any).installments).join(',')
        : '';
      return { paymentId, ICid, amount, installments: inst };
    })
    .filter((v): v is NonNullable<typeof v> => v != null);
}

function normalizePostponedPaymentsFromApi(rawList: unknown): PostponedPayments[] {
  if (!Array.isArray(rawList)) return [];
  return rawList
    .map((entry): PostponedPayments | null => {
      const paymentId = String((entry as any)?.paymentId ?? '').trim();
      const ICid = String((entry as any)?.ICid ?? '').trim();
      if (!paymentId || !ICid) return null;
      const amountRaw = (entry as any)?.amount;
      const amount =
        typeof amountRaw === 'number'
          ? amountRaw
          : Number.parseFloat(String(amountRaw ?? '0')) || 0;
      const postponeDate = String((entry as any)?.postponedDate ?? '').trim();
      const rawInst = (entry as any)?.installments;
      let installmentsStr = '';
      if (typeof rawInst === 'string') {
        installmentsStr = rawInst;
      } else if (Array.isArray(rawInst)) {
        installmentsStr = normalizeStringArray(rawInst).join(',');
      }
      return {
        paymentId,
        ICid,
        amount,
        postponedDate: postponeDate,
        installments: installmentsStr,
      };
    })
    .filter((v): v is PostponedPayments => v != null);
}

function mergePostponedWithLegacyMissed(irrPartial: Record<string, unknown>): PostponedPayments[] {
  const byPaymentId = new Map<string, PostponedPayments>();
  const fromModern = normalizePostponedPaymentsFromApi(irrPartial.postponedPayments);
  for (const p of fromModern) {
    byPaymentId.set(p.paymentId, p);
  }
  const missedMerged = normalizeLegacyMissedCollections(irrPartial.missedCollections);
  for (const m of missedMerged) {
    if (!m.paymentId || byPaymentId.has(m.paymentId)) continue;
    byPaymentId.set(m.paymentId, {
      paymentId: m.paymentId,
      ICid: m.ICid,
      amount: m.amount,
      postponedDate: '',
      installments: m.installments,
    });
  }
  return [...byPaymentId.values()];
}

function normalizePettyCash(raw: Record<string, unknown>): number {
  const v = raw.pettyCash;
  if (typeof v === 'number' && Number.isFinite(v)) {
    return Math.trunc(v);
  }
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number.parseInt(v, 10);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function normalizeIrrRecord(value: unknown): IRR {
  const raw = (value ?? {}) as Record<string, unknown>;
  const str = (k: string) => String(raw[k] ?? '').trim();
  return {
    ...(raw as unknown as Partial<IRR>),
    IRRId: str('IRRId'),
    createdAt: str('createdAt'),
    updatedAt: str('updatedAt'),
    whoAdded: str('whoAdded'),
    whoApproved: str('whoApproved') || undefined,
    approvedAt: str('approvedAt') || undefined,
    isCommissionPaid: raw.isCommissionPaid === true,
    reportType: str('reportType'),
    reportName: str('reportName'),
    reportStatus: (raw.reportStatus as IRR['reportStatus']) ?? 'created',
    pettyCash: normalizePettyCash(raw),
    employeeId: str('employeeId'),
    status: str('status'),
    companyId: str('companyId'),
    collectionDate: str('collectionDate'),
    routeIds: normalizeStringArray(raw.routeIds),
    assignedCollections: normalizeAssignedCollections(raw.assignedCollections),
    collectedPayments: normalizeCollectedPayments(raw.collectedPayments),
    collectionsOnTheWay: normalizeCollectionsOnTheWay(raw.collectionsOnTheWay),
    postponedPayments: mergePostponedWithLegacyMissed(raw),
    givenBillBooks: normalizeBillBooks(raw.givenBillBooks),
    leftOverBillBooks: normalizeBillBooks(raw.leftOverBillBooks),
    remark: str('remark') || undefined,
  };
}

export const irrService = {
  async getIrrs(params: IRRQueryParams = {}): Promise<IRRListResponse> {
    try {
      const queryParams: Record<string, any> = {
        page: params.page || 1,
        size: params.size || 10,
      };
      if (params.employeeId) queryParams.employeeId = params.employeeId;
      if (params.tripId) queryParams.tripId = params.tripId;
      if (params.routeId) queryParams.routeId = params.routeId;
      if (params.reportStatus) queryParams.reportStatus = params.reportStatus;
      if (params.collectionDateFrom) queryParams.collectionDateFrom = params.collectionDateFrom;
      if (params.collectionDateTo) queryParams.collectionDateTo = params.collectionDateTo;
      if (params.searchTerm) queryParams.searchTerm = params.searchTerm;
      if (params.startDate) queryParams.startDate = params.startDate;
      if (params.endDate) queryParams.endDate = params.endDate;
      if (params.pendingApproval === true) queryParams.pendingApproval = true;
      if (params.approvedOnly === true) queryParams.approvedOnly = true;
      if (params.pendingCommission === true) queryParams.pendingCommission = true;
      if (params.commissionPaid === true) queryParams.commissionPaid = true;
      if (params.sortBy) queryParams.sortBy = params.sortBy;
      if (params.sortDirection !== undefined) queryParams.sortDirection = params.sortDirection;

      const response = await apiService.get<unknown>('/irr', queryParams);
      const raw = response as any;
      const envelope = raw?.data?.results ? raw : raw?.data ? raw.data : raw;
      const success = Boolean(raw?.status === 'SUCCESS' || envelope?.success === true);
      const data = envelope?.data ?? envelope;

      const results = Array.isArray(data?.results) ? data.results : [];
      const normalizedResults = results.map((r: unknown) => normalizeIrrRecord(r));
      const sortedResults = [...normalizedResults].sort(
        (a: any, b: any) =>
          new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime(),
      );

      return {
        success,
        data: {
          results: sortedResults,
          totalResults: Number(data?.totalResults ?? sortedResults.length ?? 0),
          page: Number(data?.page ?? queryParams.page ?? 1),
          size: Number(data?.size ?? queryParams.size ?? 10),
          totalPages: Number(data?.totalPages ?? 1),
        },
      };
    } catch (error) {
      console.error('[IRR-SERVICE] Error fetching IRRs:', error);
      throw error;
    }
  },

  async getIrrById(irrId: string): Promise<IRRResponse> {
    try {
      const response = await apiService.get<unknown>(`/irr/${irrId}`);
      const unwrapped = unwrapIrrEnvelope(response);
      return { success: unwrapped.success, data: normalizeIrrRecord(unwrapped.data) };
    } catch (error) {
      console.error('[IRR-SERVICE] Error fetching IRR by ID:', error);
      throw error;
    }
  },

  async createIrr(dto: Omit<CreateIRRDto, 'whoAdded'>): Promise<IRRResponse> {
    try {
      const employeeId = requireEmployeeId();
      const payload = { ...dto, whoAdded: employeeId };
      const response = await apiService.post<Record<string, unknown>>('/irr', payload);
      const raw = response as any;
      const inner = raw?.data ?? response;
      const success = raw?.status === 'SUCCESS' || inner?.success === true;
      const data = inner?.data ?? inner;
      return { success: !!success, data: normalizeIrrRecord(data) };
    } catch (error) {
      console.error('[IRR-SERVICE] Error creating IRR:', error);
      throw error;
    }
  },

  async partialUpdateIrr(irrId: string, dto: UpdateIRRDto): Promise<IRRResponse> {
    try {
      const whoUpdated = requireEmployeeId();
      const response = await apiService.patch<unknown>(`/irr/${irrId}`, {
        ...dto,
        whoUpdated,
      });
      const unwrapped = unwrapIrrEnvelope(response);
      return { success: unwrapped.success, data: normalizeIrrRecord(unwrapped.data) };
    } catch (error) {
      console.error('[IRR-SERVICE] Error updating IRR:', error);
      throw error;
    }
  },

  async approveIrr(irrId: string): Promise<IRRResponse> {
    try {
      const response = await apiService.patch<unknown>(`/irr/${irrId}/approve`, {});
      const unwrapped = unwrapIrrEnvelope(response);
      return { success: unwrapped.success, data: normalizeIrrRecord(unwrapped.data) };
    } catch (error) {
      console.error('[IRR-SERVICE] Error approving IRR:', error);
      throw error;
    }
  },

  async completeIrr(irrId: string): Promise<IRRResponse> {
    try {
      const response = await apiService.patch<unknown>(`/irr/${irrId}/complete`, {});
      const unwrapped = unwrapIrrEnvelope(response);
      return { success: unwrapped.success, data: normalizeIrrRecord(unwrapped.data) };
    } catch (error) {
      console.error('[IRR-SERVICE] Error completing IRR:', error);
      throw error;
    }
  },

  async cancelIrr(irrId: string): Promise<IRRResponse> {
    try {
      const response = await apiService.patch<unknown>(`/irr/${irrId}/cancel`, {});
      const unwrapped = unwrapIrrEnvelope(response);
      return { success: unwrapped.success, data: normalizeIrrRecord(unwrapped.data) };
    } catch (error) {
      console.error('[IRR-SERVICE] Error canceling IRR:', error);
      throw error;
    }
  },

  async postponeAssigned(irrId: string, dto: PostponeIrrAssignedDto): Promise<IRRResponse> {
    try {
      const response = await apiService.patch<unknown>(
        `/irr/${irrId}/postpone-assigned`,
        dto,
      );
      const unwrapped = unwrapIrrEnvelope(response);
      return { success: unwrapped.success, data: normalizeIrrRecord(unwrapped.data) };
    } catch (error) {
      console.error('[IRR-SERVICE] Error postponing assigned installments:', error);
      throw error;
    }
  },

  /** Postpone one NEUTRAL installment on a card (recorded on the IRR when `irrId` has it assigned). */
  async postponeCardInstallment(dto: PostponeCardInstallmentDto): Promise<PostponeCardInstallmentResult> {
    try {
      const response = await apiService.patch<unknown>('/irr/postpone-card-installment', dto);
      return unwrapIrrEnvelope<PostponeCardInstallmentResult>(response).data;
    } catch (error) {
      console.error('[IRR-SERVICE] Error postponing card installment:', error);
      throw error;
    }
  },

  async setCardPaymentDay(irrId: string, dto: SetIrrCardPaymentDayDto): Promise<IRRResponse> {
    try {
      const response = await apiService.patch<unknown>(
        `/irr/${irrId}/set-card-payment-day`,
        dto,
      );
      const unwrapped = unwrapIrrEnvelope(response);
      return { success: unwrapped.success, data: normalizeIrrRecord(unwrapped.data) };
    } catch (error) {
      console.error('[IRR-SERVICE] Error setting card payment day:', error);
      throw error;
    }
  },

  async addCollectedPayment(irrId: string, dto: AddCollectedPaymentDto): Promise<IRRResponse> {
    try {
      const response = await apiService.patch<unknown>(
        `/irr/${irrId}/add-collected-payment`,
        dto,
      );
      const unwrapped = unwrapIrrEnvelope(response);
      return { success: unwrapped.success, data: normalizeIrrRecord(unwrapped.data) };
    } catch (error) {
      console.error('[IRR-SERVICE] Error adding collected payment:', error);
      throw error;
    }
  },

  async addAssignedCards(irrId: string, dto: AddAssignedCardsDto): Promise<IRRResponse> {
    try {
      const response = await apiService.patch<unknown>(
        `/irr/${irrId}/add-assigned-cards`,
        dto,
      );
      const unwrapped = unwrapIrrEnvelope(response);
      return { success: unwrapped.success, data: normalizeIrrRecord(unwrapped.data) };
    } catch (error) {
      console.error('[IRR-SERVICE] Error adding assigned cards:', error);
      throw error;
    }
  },

  async addCollectionsOnTheWay(
    irrId: string,
    dto: AddCollectionsOnTheWayDto,
  ): Promise<IRRResponse> {
    try {
      const response = await apiService.patch<unknown>(
        `/irr/${irrId}/add-collections-on-the-way`,
        dto,
      );
      const unwrapped = unwrapIrrEnvelope(response);
      return { success: unwrapped.success, data: normalizeIrrRecord(unwrapped.data) };
    } catch (error) {
      console.error('[IRR-SERVICE] Error adding collections on the way:', error);
      throw error;
    }
  },

  async deleteIrr(irrId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiService.delete<{ success: boolean; message: string }>(
        `/irr/${irrId}`,
      );
      return response;
    } catch (error) {
      console.error('[IRR-SERVICE] Error deleting IRR:', error);
      throw error;
    }
  },

  async listEligibleInstallments(
    dto: ListEligibleInstallmentsDto,
  ): Promise<EligibleInstallmentsResponse> {
    try {
      const response = await apiService.post<unknown>(
        '/irr/eligible-installments',
        dto,
        { timeout: IRR_ELIGIBLE_INSTALLMENTS_TIMEOUT_MS },
      );
      const unwrapped = unwrapIrrEnvelope(response);
      const raw = (unwrapped.data ?? {}) as Record<string, unknown>;
      return {
        success: unwrapped.success,
        data: {
          installments: normalizeEligibleInstallmentRows(raw.installments),
        },
      };
    } catch (error) {
      console.error('[IRR-SERVICE] Error fetching eligible installments:', error);
      throw error;
    }
  },

  async previewRecoveryCommission(
    dto: RecoveryCommissionApplicationRequest,
  ): Promise<RecoveryCommissionPreviewResponse> {
    const body = await apiService.post<{
      success: boolean;
      data: RecoveryCommissionPreviewResponse;
    }>('/irr/recovery-commission/preview', dto);
    const outer = body as unknown as {
      data?: RecoveryCommissionPreviewResponse | { data?: RecoveryCommissionPreviewResponse };
    };
    const payload =
      outer?.data && 'ok' in outer.data
        ? outer.data
        : (outer?.data as { data?: RecoveryCommissionPreviewResponse })?.data;
    if (!payload || typeof payload.ok !== 'boolean') {
      throw new Error('Invalid recovery commission preview response');
    }
    return payload;
  },

  async applyRecoveryCommission(
    dto: RecoveryCommissionApplicationRequest,
  ): Promise<RecoveryCommissionApplyResponse> {
    const body = await apiService.post<{
      success: boolean;
      data: RecoveryCommissionApplyResponse;
    }>('/irr/recovery-commission/apply', dto);
    const outer = body as unknown as {
      data?: RecoveryCommissionApplyResponse | { data?: RecoveryCommissionApplyResponse };
    };
    const payload =
      outer?.data && 'remunerationId' in outer.data
        ? outer.data
        : (outer?.data as { data?: RecoveryCommissionApplyResponse })?.data;
    if (!payload?.remunerationId) {
      throw new Error('Invalid recovery commission apply response');
    }
    return payload;
  },
};
